<script lang="ts" setup>
import { useAdmissionReleaseInfo } from '@sancare/typescript-commons/src/composables/useAdmissionReleaseInfo'
import dayjs from '@sancare/typescript-commons/src/misc/dayjs'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

import { Rhs } from '@/store/modules/stay/smr/types'

const { t } = useI18n()

const props = defineProps<{
  rhs: Rhs
  rhsIndex?: number
  totalRhs?: number
}>()

const {
  admissionMode,
  releaseMode,
  origin,
  destination,
  admissionTooltip,
  releaseTooltip,
} = useAdmissionReleaseInfo(() => ({
  admissionMode: props.rhs.admissionMode,
  releaseMode: props.rhs.releaseMode,
  origin: props.rhs.origin,
  destination: props.rhs.destination,
}), undefined, t)

const medicalUnitTooltip = computed(() => {
  const um = props.rhs.medicalUnit
  if (!um) return null
  const lines: string[] = []
  if (um.title) lines.push(um.title)
  const division = um.facilityDivision
  if (division?.title || division?.facility?.name) {
    lines.push([division?.title, division?.facility?.name].filter(Boolean).join(' — '))
  }
  return lines.join('<br />') || null
})
</script>

<template>
  <div class="d-flex flex-column gap-2">
    <span
      v-if="rhsIndex !== undefined && totalRhs !== undefined"
      class="ms-0 text-muted small"
    >
      {{ t('stayDisplayer.rhs') }} {{ rhsIndex + 1 }} {{ t('outOf') }} {{ totalRhs }}
    </span>
    <span class="ms-0 fw-bold text-nowrap">
      {{ dayjs(rhs.startDate).format('DD MMM YYYY') }} - {{ dayjs(rhs.endDate).format('DD MMM YYYY') }}
    </span>
    <span class="ms-0">
      {{ t('stayDisplayer.weekShort') }} {{ rhs.week.nb }}/{{ rhs.week.year }} -
      <span
        v-tooltip="medicalUnitTooltip ? { content: medicalUnitTooltip, placement: 'top', html: true } : null"
        :class="{ 'cursor-pointer': medicalUnitTooltip }"
      >
        {{ t('stayDisplayer.medicalUnitShort') }}&nbsp;: {{ rhs.medicalUnit ? rhs.medicalUnit.reference : '-' }}
      </span>
    </span>
    <span class="ms-0">
      <span
        v-tooltip="{
          content: admissionTooltip,
          placement: 'top',
          html: true
        }"
        class="cursor-pointer"
      >
        {{ t('stayDisplayer.generalStayData.entryDate') }}:
        {{ admissionMode }}/{{ origin }}
      </span>
      —
      <span
        v-tooltip="{
          content: releaseTooltip,
          placement: 'top',
          html: true
        }"
        class="cursor-pointer"
      >
        {{ t('stayDisplayer.generalStayData.exitDate') }}:
        {{ releaseMode }}/{{ destination }}
      </span>
    </span>
  </div>
</template>
